import { ComponentHealthCondition } from 'features/fde'
import { MapFeaturePattern, UnitDisplayPropertiesFn } from 'features/siteMapViz'
import {
  AggregateUnitData,
  getExtendedHealthScore,
  NoRLHealthScore,
} from 'features/siteView/aggregateSiteData'
import { isNil } from 'lodash'
import { useCallback } from 'react'
import { TraditionalInspectionTypes } from 'types'

import { useSiteViewColors } from './siteViewTheme'

/**
 * Returns a function that takes a unit ID and returns the color and pattern that should be used to represent that unit.
 */
export function useGetUnitDisplayPropertiesFn(
  aggregateData: ReadonlyArray<AggregateUnitData>,
  isHealthDataLoading: boolean,
  selectedYear: number
): UnitDisplayPropertiesFn {
  const colors = useSiteViewColors()

  return useCallback(
    (unitID: string | null) => {
      if (isNil(unitID))
        return { color: colors.noUnitData, pattern: MapFeaturePattern.Flat }

      const unitData = aggregateData.find(
        (unit) => unit.unitSpecs.unitData.id === unitID
      )

      if (!unitData) {
        return { color: colors.noUnitData, pattern: MapFeaturePattern.Flat }
      }

      if (isHealthDataLoading) {
        return {
          color: colors[NoRLHealthScore.NotEnoughData],
          pattern: MapFeaturePattern.Flat,
        }
      }

      const minRLFromYear = unitData.health[selectedYear]?.minRLFromYear
      const riskScore = unitData.health[selectedYear]?.riskScore

      const healthScore = getExtendedHealthScore(
        minRLFromYear,
        riskScore,
        !!unitData.mostRecentInspectionData.inspection
      )

      if (
        Object.values(ComponentHealthCondition).includes(
          healthScore as ComponentHealthCondition
        )
      ) {
        const inspectionType =
          unitData.mostRecentInspectionData.inspection?.data.inspection_type
        const pattern = TraditionalInspectionTypes.includes(
          inspectionType ?? ''
        )
          ? MapFeaturePattern.Stripes // Traditional (old way of doing things) pattern (shows its "sparse")
          : MapFeaturePattern.Flat // AIR pattern

        return { color: colors[healthScore], pattern }
      }

      if (isNil(unitData.mostRecentInspectionData.inspection)) {
        return {
          color: colors[healthScore],
          pattern: MapFeaturePattern.Flat,
        }
      }

      return {
        color: colors[healthScore],
        pattern: MapFeaturePattern.Checkers, // Not enough data pattern
      }
    },
    [colors, aggregateData, isHealthDataLoading, selectedYear]
  )
}
